
import React from 'react';
import { Pill, Clock, Bell, Calendar, ArrowRight, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';

export const MobileMedicationManagementSection = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Pill,
      title: "Suivi Médicaments",
      description: "Gérez vos traitements en toute simplicité"
    },
    {
      icon: Bell,
      title: "Rappels Intelligents",
      description: "Ne manquez jamais une prise"
    },
    {
      icon: Calendar,
      title: "Planification",
      description: "Organisez vos prises quotidiennes"
    }
  ];

  const stats = [
    { value: "100%", label: "Fiabilité" },
    { value: "24/7", label: "Surveillance" },
    { value: "5k+", label: "Utilisateurs" }
  ];

  return (
    <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-2xl p-6 mx-4 mb-6 overflow-hidden relative">
      {/* Background animations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-4 right-4 w-20 h-20 bg-green-200/30 rounded-full animate-pulse-opacity" />
        <div className="absolute bottom-8 left-8 w-16 h-16 bg-emerald-200/30 rounded-full animate-float" />
        <div className="absolute top-1/2 right-1/3 w-12 h-12 bg-teal-200/30 rounded-full animate-molecule-float" />
      </div>

      {/* Header */}
      <div className="relative z-10 text-center mb-6">
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center animate-brain-wave shadow-lg">
              <Pill className="h-8 w-8 text-white" />
            </div>
            <div className="absolute -inset-1 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl opacity-30 animate-pulse-wave" />
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Gestion de Médicaments
        </h2>
        <p className="text-gray-600 text-sm leading-relaxed">
          Prenez le contrôle de vos traitements. Rappels automatiques, 
          suivi des prises et gestion complète de vos médicaments.
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 gap-3 mb-6 relative z-10">
        {features.map((feature, index) => (
          <Card 
            key={index} 
            className="p-4 bg-white/70 backdrop-blur-sm border-0 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.02]"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
                <feature.icon className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-sm">{feature.title}</h3>
                <p className="text-gray-600 text-xs">{feature.description}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6 relative z-10">
        {stats.map((stat, index) => (
          <div key={index} className="text-center">
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-3 shadow-sm">
              <div className="text-lg font-bold text-green-600">{stat.value}</div>
              <div className="text-xs text-gray-600">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA Button */}
      <div className="relative z-10">
        <Button 
          onClick={() => navigate('/medicaments-mobile')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group border-0"
        >
          <span>Gérer mes Médicaments</span>
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
        </Button>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-green-200/20 to-transparent rounded-bl-full" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-emerald-200/20 to-transparent rounded-tr-full" />
    </div>
  );
};
